import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useGetContacts } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetContacts";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import useCompanyAutocompleteQuery from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteQuery";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const useEmailAgentDefaultContext = (
    onCompanyChange: (company: AiAgentEmailAutocompleteCompany) => void,
    onRecipientChange: (v: Contact | null) => void,
    onGenerate: VoidFunction,
) => {
    const {
        params: { domain, recipient: recipientNameParam, autogenerate },
    } = useCurrentStateAndParams<{ domain: string; recipient: string; autogenerate: boolean }>();
    const [isRecipientSynced, setIsRecipientSynced] = useState(false);
    const [isCompanySynced, setIsCompanySynced] = useState(false);
    const hasTriggeredAutogenerateRef = useRef(false);
    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);
    const { isContactsVisible, isContactsStatusFetched } = useIsContactsAvailable();
    const shouldUseContactsSearch = isContactsStatusFetched && isContactsVisible;

    useEffect(() => {
        if (isBlocked && domain) {
            void proNavigator.updateParams({ domain: undefined, recipient: undefined, autogenerate: undefined });
        }
    }, [isBlocked, domain]);

    const { data: domainCompanyData } = useCompanyAutocompleteQuery(isBlocked ? "" : domain);

    const { data: contactSearchResults } = useGetContacts({
        domain,
        enabled: !isBlocked && !!domain && !!recipientNameParam && shouldUseContactsSearch,
        search: recipientNameParam,
    });

    useEffect(() => {
        if (!domain || !domainCompanyData) {
            return;
        }
        setIsCompanySynced(true);

        const companyMatch = domainCompanyData.find((item) => item.name.toLowerCase() === domain.toLowerCase());
        if (companyMatch?.companyInfo) {
            const { mainDomain, companyName, image } = companyMatch.companyInfo;
            onCompanyChange({ domain: mainDomain, companyName, image, isCompanyLevel: true });
            return;
        }

        const domainMatch = domainCompanyData
            .flatMap((item) => item.domainsInfo || [])
            .find((d) => d.name.toLowerCase() === domain.toLowerCase());
        if (domainMatch) {
            onCompanyChange({
                domain: domainMatch.name,
                companyName: domainMatch.name,
                image: domainMatch.image,
                isCompanyLevel: false,
            });
            return;
        }

        onCompanyChange({ domain, companyName: domain, isCompanyLevel: false });
    }, [domain, domainCompanyData]);

    useEffect(() => {
        if (!domainCompanyData || !isCompanySynced) {
            return;
        }

        if (recipientNameParam) {
            if (shouldUseContactsSearch) {
                if (!contactSearchResults) {
                    setIsRecipientSynced(false);
                    return;
                }

                if (!isRecipientSynced) {
                    if (contactSearchResults.length > 0) {
                        const matchingContact = contactSearchResults[0];
                        onRecipientChange(matchingContact);
                    } else {
                        onRecipientChange({ name: recipientNameParam });
                    }
                    setIsRecipientSynced(true);
                }
            } else if (!isRecipientSynced) {
                onRecipientChange({ name: recipientNameParam });
                setIsRecipientSynced(true);
            }
        } else {
            if (isRecipientSynced) {
                setIsRecipientSynced(false);
            }
        }

        if (!autogenerate || hasTriggeredAutogenerateRef.current) {
            return;
        }

        if (recipientNameParam && !isRecipientSynced) {
            return;
        }

        hasTriggeredAutogenerateRef.current = true;
        AIAgentTrackingService.email.trackGenerateEmail();
        void proNavigator.updateParams({ autogenerate: undefined, recipient: undefined, domain: undefined });
        onGenerate();
    }, [
        domainCompanyData,
        isCompanySynced,
        recipientNameParam,
        contactSearchResults,
        autogenerate,
        isRecipientSynced,
        onGenerate,
        shouldUseContactsSearch,
    ]);
};

export default useEmailAgentDefaultContext;
